
import { apiService } from "@services/api.service";
import { Vue, Component, Prop } from "vue-property-decorator";
import InlineSvg from "vue-inline-svg";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { TranslateResult } from "vue-i18n";
import { buildURL } from "@utils/build-url";
import { useLoader } from "@composables/loader";
import CiInformationContent from "@components/users/onboarding/register/InformationContent.vue";
import CiConfirmEmail from "@components/users/onboarding/register/ConfirmEmail.vue";
import { settingsService } from "@services/settings.service";
import CiOmniauthButtons from "@components/base/omniauth-buttons.vue";
import CiPartnerButtons from "@components/base/PartnerButtons.vue";

@Component({
  name: "ci-registration-form",
  components: {
    "ci-confirm-email": CiConfirmEmail,
    "ci-information-content": CiInformationContent,
    "ci-omniauth-buttons": CiOmniauthButtons,
    "ci-partner-buttons": CiPartnerButtons,
    "inline-svg": InlineSvg,
  },
})
export default class extends Vue {
  @Prop({ default: true }) inModalContext!: boolean;
  @Prop({ default: "" }) onboardingType!: string;
  @Prop({ default: "" }) gatingBannerLocation!: string;
  @Prop({ default: "" }) returnButtonText!: string;
  @Prop({ default: false }) articleRegistration!: boolean;
  @Prop({ default: () => window.location.href || null }) registrationReferrer!: Nullable<string>;
  @Prop({ default: false }) redirect!: boolean;

  GTM_EVENT_CATEGORY = "Registration";

  public email: Nullable<string> = null;
  public password: string = "";
  public middleName: string = "";
  public invisibleCaptchaSpinner: string = "";
  public acceptLatestTosVersion: boolean = false;
  public acceptLatestPrivacyVersion: boolean = false;
  public errors: Nullable<Indexable> = null;
  public userIsRegistered: boolean = false;
  public disableButton: boolean = false;
  private loader = useLoader();

  public get isDisabled(): boolean {
    return !this.acceptLatestTosVersion || !this.acceptLatestPrivacyVersion || this.disableButton;
  }

  public get backLinkText(): TranslateResult {
    return this.returnButtonText === ""
      ? this.$t("ui.components.users.onboarding.register.back_to_default")
      : this.$t(this.returnButtonText);
  }

  public get providers(): Settings.Provider[] {
    return settingsService.options.registration.omniauth.providers;
  }

  public get partnersEnabled(): boolean {
    return settingsService.options.region.code === "DE";
  }

  mounted() {
    apiService.registrations.get().then((response) => {
      this.invisibleCaptchaSpinner = response.data.invisible_captcha_spinner;
    });
  }

  public async submit(): Promise<void> {
    this.disableButton = true;
    try {
      this.loader.start();
      const params = {
        user: {
          email: this.email,
          password: this.password,
          accept_latest_privacy_version: this.acceptLatestPrivacyVersion,
          accept_latest_tos_version: this.acceptLatestTosVersion,
          onboarding_type: this.onboardingType,
          gating_banner_location: this.gatingBannerLocation,
          middlename: this.middleName,
          gating: this.onboardingType === "gating-banner",
        },
      };

      const response = await apiService.axios.post(this.$settings.options.registration.path, params);
      await this.trackRegistrationSuccess(response.data.user.provider);
      apiService.refreshSettings();
      this.userIsRegistered = true;
      if (this.redirect) {
        const searchParams: Indexable<string> = document.referrer ? { redirect_url: document.referrer } : {};
        window.location.href = buildURL("/onboarding/users/sign_up_success", {
          searchParams,
        });
      }
    } catch (err: any) {
      this.resetCheckboxes();
      this.errors = JSON.parse(err.request.response).errors;
    } finally {
      this.disableButton = false;
      this.loader.stop();
    }
  }

  public openLoginModal(): void {
    if (this.inModalContext) {
      this.$bvModal.show("modal-login");
      this.$bvModal.hide("modal-registration");
    } else {
      window.location.href = buildURL("/users/sign_in");
    }
  }

  private resetCheckboxes(): void {
    this.acceptLatestTosVersion = false;
    this.acceptLatestPrivacyVersion = false;
  }

  private async trackRegistrationSuccess(provider: Nullable<string>): Promise<void> {
    gtmTrackingService.track({
      eventCategory: this.GTM_EVENT_CATEGORY,
      eventAction: `${this.GTM_EVENT_CATEGORY}Successful`,
      eventLabel: {
        onboarding_type: this.onboardingType,
        registration_type: provider || "email",
        gating_banner_location: this.gatingBannerLocation,
      },
    });
  }
}
